// Build and download a structured JSON snapshot of a Chart Browser view.
//
// This complements reportExport.js (the per-app market report) with a chart
// snapshot: the current top-chart listing for one country + chart type, in the
// exact order the user is viewing it. Everything is built in-browser from the
// in-memory entries — no backend, no extra network calls. The download itself
// reuses downloadReport() from reportExport.js so there is a single JSON-blob
// trigger for the whole app.

import { STOREFRONTS } from '../data/storefronts.js';

/**
 * Shape of a single chart entry in the exported snapshot. Mirrors the
 * in-memory ChartBrowserEntry exactly — no derived or estimated fields.
 * @typedef {object} ChartReportEntry
 * @property {number|null} rank - 1-based chart position as shown.
 * @property {number|null} trackId - Apple app id (numeric).
 * @property {string|null} name
 * @property {string|null} artistName - Developer / seller name.
 * @property {string|null} artworkUrl
 * @property {string|null} primaryGenre
 * @property {string|null} genreId
 */

/**
 * Shape of the exported chart-snapshot document. Kept parallel to MarketReport
 * (reportExport.js): a `tool` stamp, an ISO `exportedAt`, a subject metadata
 * block (`chart` here, `app` there), the ordered list (`entries`/`regions`),
 * and a small `summary`.
 * @typedef {object} ChartReport
 * @property {string} tool
 * @property {string} reportType  Discriminator: always 'chart-snapshot'.
 * @property {string} exportedAt  ISO-8601 timestamp.
 * @property {object} chart  { country, countryName, type }
 * @property {ChartReportEntry[]} entries  In displayed order.
 * @property {object} summary  { totalEntries }
 */

/**
 * Resolve a storefront code to its stable English name. Uses the static
 * STOREFRONTS table (no i18n) so the exported data is locale-independent and
 * deterministic. Falls back to the upper-cased code for unknown codes.
 * @param {string} code - lower-cased storefront code.
 * @returns {string|null}
 */
function resolveCountryName(code) {
  if (!code) return null;
  const hit = STOREFRONTS.find((sf) => sf.code === code);
  return hit ? hit.name : code.toUpperCase();
}

/**
 * Build a ChartReport object from the currently loaded chart entries.
 *
 * The caller passes the entries in their current on-screen order (after any
 * client-side sort), so the export always reflects what the user sees.
 *
 * @param {string|null|undefined} country - storefront code, e.g. "us".
 * @param {string|null|undefined} type - chart type, e.g. "top-free".
 * @param {import('./chartRank.js').ChartBrowserEntry[]} entries - ordered entries.
 * @param {string} exportedAt - ISO-8601 timestamp (injected so tests stay deterministic).
 * @returns {ChartReport}
 */
export function buildChartReport(country, type, entries, exportedAt) {
  const code = typeof country === 'string' ? country.trim().toLowerCase() : '';
  const list = Array.isArray(entries) ? entries : [];

  const mapped = list.map((entry) => ({
    rank: entry?.rank ?? null,
    trackId: entry?.trackId ?? null,
    name: entry?.name ?? null,
    artistName: entry?.artistName ?? null,
    artworkUrl: entry?.artworkUrl ?? null,
    primaryGenre: entry?.primaryGenre ?? null,
    genreId: entry?.genreId ?? null,
  }));

  return {
    tool: 'App Radar',
    reportType: 'chart-snapshot',
    exportedAt,
    chart: {
      country: code || null,
      countryName: resolveCountryName(code),
      type: typeof type === 'string' && type ? type : null,
    },
    entries: mapped,
    summary: {
      totalEntries: mapped.length,
    },
  };
}

/**
 * Build a download filename for a chart snapshot. Always lower-case and
 * filesystem-safe; includes country + chart type + date as required.
 * @param {string|null|undefined} country - storefront code, e.g. "us".
 * @param {string|null|undefined} type - chart type, e.g. "top-free".
 * @param {string} dateStr - e.g. "2026-06-18".
 * @returns {string}
 */
export function buildChartReportFilename(country, type, dateStr) {
  const slug = (value, fallback) => {
    const cleaned = String(value ?? '')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-|-$/g, '');
    return cleaned || fallback;
  };
  const countrySlug = slug(country, 'xx');
  const typeSlug = slug(type, 'chart');
  return `app-radar-chart-${countrySlug}-${typeSlug}-${dateStr}.json`;
}
